import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    FileText, ArrowLeft, Download, FolderOpen, Loader2, AlertTriangle,
    User, Mail, MapPin, Calendar, Tag, ShieldAlert, Link2, Archive, RotateCcw, Search, X,
} from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";

/**
 * OPS-DOC-001 · Operations → Documents detail view.
 *
 * DEV-025 (extension) · Adds Relink / Archive / Restore actions so
 * an authorised operations user can complete the review journey for
 * an orphaned document that carries real file bytes. All three
 * actions re-validate permissions + associations on the server; the
 * file itself is never deleted.
 */
function humanDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function humanSize(bytes) {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ARCHIVE_REASON_DEFAULT = "Orphaned document — original project/customer unavailable";

export default function OperationsDocumentDetail() {
    const { docId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const listReturnQuery = location.search || "";

    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [opening, setOpening] = useState(false);

    const [relinkOpen, setRelinkOpen] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [restoreBusy, setRestoreBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const data = await api.opsGetDocument(docId);
            setDoc(data);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                toast.error("That document no longer exists.");
                navigate(`/operations/documents${listReturnQuery}`, { replace: true });
                return;
            }
            const msg = formatApiError(e?.response?.data?.detail) || "Failed to load document";
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [docId, navigate, listReturnQuery]);

    useEffect(() => { load(); }, [load]);

    const backHref = `/operations/documents${listReturnQuery}`;

    const openDocument = async () => {
        if (!doc || opening) return;
        setOpening(true);
        try {
            const { token: dt } = await api.documentDownloadToken(doc.id);
            window.open(
                `${api.documentDownloadUrl(doc.id)}?dt=${encodeURIComponent(dt)}`,
                "_blank",
                "noopener,noreferrer",
            );
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Download failed");
        } finally {
            setOpening(false);
        }
    };

    const projectHref = doc?.project_id && doc?.project_exists !== false
        ? `/operations/projects/${encodeURIComponent(doc.project_id)}`
        : null;
    const projectMissing = !!doc?.project_id && doc?.project_exists === false;
    const customerMissing = doc?.customer_exists === false && doc?.customer_id !== null && doc?.project_id;
    const legacyUnlinked = !doc?.project_id;
    const fileAvailable = !!doc?.has_file && (doc?.file_size ?? 0) > 0;
    const isArchived = !!doc?.is_archived;
    const needsReview = !isArchived && (projectMissing || customerMissing || legacyUnlinked);

    const onRestore = async () => {
        if (!doc || restoreBusy) return;
        setRestoreBusy(true);
        try {
            const updated = await api.opsRestoreDocument(doc.id);
            setDoc(updated);
            toast.success("Document restored.");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Restore failed";
            toast.error(message);
        } finally {
            setRestoreBusy(false);
        }
    };

    return (
        <OperationsShell>
            <PageContainer width="full" testid="ops-document-detail-page">
                <div className="mb-4">
                    <Link
                        to={backHref}
                        data-testid="ops-document-back"
                        className="inline-flex items-center gap-1.5 text-xs text-[#4B5565] hover:text-[#131B2F]"
                    >
                        <ArrowLeft size={12} /> Back to Documents
                    </Link>
                </div>
                <PageHeader
                    eyebrow="OPERATIONS"
                    title={doc?.title || doc?.file_name || (loading ? "Loading…" : "Document")}
                    subtitle={doc?.category || ""}
                    icon={FileText}
                    testid="ops-document-detail"
                />

                {loading && (
                    <div className="rounded-xl border border-[#E5E7EB] bg-white p-10 flex items-center justify-center text-sm text-[#4B5565]" data-testid="ops-document-loading">
                        <Loader2 className="animate-spin mr-2" size={16} /> Loading document…
                    </div>
                )}

                {!loading && err && (
                    <div className="rounded-xl border border-[#E5E7EB] bg-white p-10 text-center" data-testid="ops-document-error">
                        <AlertTriangle size={22} className="mx-auto text-[#B91C1C]" />
                        <div className="mt-3 text-sm text-[#131B2F]">{err}</div>
                    </div>
                )}

                {!loading && !err && doc && (
                    <div className="space-y-4">
                        {/* Primary actions */}
                        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
                            <div className="flex-1">
                                <div className="text-xs text-[#4B5565]">
                                    <span
                                        className={`text-[10px] px-2 py-0.5 rounded-full ${isArchived ? "bg-[#F5F5F7] text-[#4B5565]" : "bg-[#EAF6E1] text-[#3E9E2E]"}`}
                                        data-testid="ops-document-status-badge"
                                    >
                                        {isArchived ? "Archived" : "Active"}
                                    </span>
                                    {doc.visibility && (
                                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F5F5F7] text-[#4B5565]">
                                            {doc.visibility === "customer_visible" ? "Customer visible" : "Internal only"}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 text-sm text-[#131B2F]">
                                    {doc.file_name || "—"} · {humanSize(doc.file_size)} · {doc.file_type || "unknown type"}
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={openDocument}
                                    disabled={opening || !fileAvailable}
                                    title={fileAvailable ? undefined : "No file content is available for this document."}
                                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#131B2F] text-white text-sm hover:bg-[#0B1120] disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="ops-document-open"
                                >
                                    {opening ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                    Open / download
                                </button>
                                {projectHref ? (
                                    <Link
                                        to={projectHref}
                                        state={{ returnTo: `${location.pathname}${location.search}` }}
                                        data-testid="ops-document-open-project"
                                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                    >
                                        <FolderOpen size={14} /> Open project
                                    </Link>
                                ) : (
                                    <span
                                        data-testid="ops-document-open-project-disabled"
                                        data-reason={projectMissing ? "project_missing" : legacyUnlinked ? "legacy_unlinked" : "unknown"}
                                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-dashed border-[#E5E7EB] bg-[#FAFAFA] text-sm text-[#9CA3AF] cursor-not-allowed"
                                        title={
                                            projectMissing
                                                ? "Project no longer available."
                                                : legacyUnlinked
                                                    ? "This document is not linked to a project."
                                                    : undefined
                                        }
                                    >
                                        <FolderOpen size={14} />
                                        {projectMissing ? "Project no longer available" : "Open project"}
                                    </span>
                                )}

                                {/* DEV-025 (extension) · Review actions */}
                                {!isArchived && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setRelinkOpen(true)}
                                            data-testid="ops-document-relink-btn"
                                            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-[#131B2F]/60 bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                        >
                                            <Link2 size={14} /> Relink to project
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setArchiveOpen(true)}
                                            data-testid="ops-document-archive-btn"
                                            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-[#B45309]/60 bg-white text-sm text-[#B45309] hover:bg-[#FFFBEB]"
                                        >
                                            <Archive size={14} /> Archive as orphaned
                                        </button>
                                    </>
                                )}
                                {isArchived && (
                                    <button
                                        type="button"
                                        onClick={onRestore}
                                        disabled={restoreBusy}
                                        data-testid="ops-document-restore-btn"
                                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-[#131B2F]/60 bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-50"
                                    >
                                        {restoreBusy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                                        Restore
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* DEV-025 (extension) · Reference-health notices now
                            point to the actions above rather than repeat a
                            circular "please review" instruction. */}
                        {isArchived && (
                            <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFC] p-4 flex items-start gap-2" data-testid="ops-document-refhealth-archived">
                                <Archive size={16} className="text-[#4B5565] mt-0.5" />
                                <div className="text-xs text-[#4B5565]">
                                    <div className="font-semibold text-[#131B2F]">This document is archived.</div>
                                    <div className="mt-0.5">
                                        The file is preserved. Use <span className="font-semibold">Restore</span> above to return it to the active list. If its references are still missing after restore, it will re-appear in the Needs review queue.
                                        {doc.archive_reason && (
                                            <div className="mt-1 text-[11px] text-[#4B5565]">Reason: <span className="font-mono text-[#131B2F]">{doc.archive_reason}</span></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isArchived && projectMissing && (
                            <div className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-4 flex items-start gap-2" data-testid="ops-document-refhealth-project-missing">
                                <ShieldAlert size={16} className="text-[#B91C1C] mt-0.5" />
                                <div className="text-xs text-[#7C1D1D]">
                                    <div className="font-semibold text-[#7C1D1D]">Project no longer available.</div>
                                    <div className="mt-0.5">
                                        The stored project reference (<code className="font-mono">{doc.project_id}</code>) no longer resolves. Use <span className="font-semibold">Relink to project</span> above to point this file at the correct live project, or <span className="font-semibold">Archive as orphaned</span> if no relink is possible. The file is preserved either way.
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isArchived && customerMissing && !projectMissing && (
                            <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4 flex items-start gap-2" data-testid="ops-document-refhealth-customer-missing">
                                <ShieldAlert size={16} className="text-[#B45309] mt-0.5" />
                                <div className="text-xs text-[#78350F]">
                                    <div className="font-semibold text-[#78350F]">Customer record missing.</div>
                                    <div className="mt-0.5">
                                        The linked project no longer references a canonical customer record. Project, address and file download remain intact. Use <span className="font-semibold">Relink to project</span> above if the correct project has since been recreated.
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isArchived && legacyUnlinked && (
                            <div className="rounded-xl border border-[#C7D2FE] bg-[#EEF2FF] p-4 flex items-start gap-2" data-testid="ops-document-refhealth-legacy">
                                <ShieldAlert size={16} className="text-[#1E3A8A] mt-0.5" />
                                <div className="text-xs text-[#1E3A8A]">
                                    <div className="font-semibold">Legacy document — no canonical relationship.</div>
                                    <div className="mt-0.5">
                                        This document was uploaded without a project reference. Use <span className="font-semibold">Relink to project</span> to attach it to the correct project, or <span className="font-semibold">Archive as orphaned</span> to remove it from the active list while preserving the file.
                                    </div>
                                </div>
                            </div>
                        )}
                        {!fileAvailable && !isArchived && (
                            <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-4 flex items-start gap-2" data-testid="ops-document-refhealth-file-missing">
                                <ShieldAlert size={16} className="text-[#4B5565] mt-0.5" />
                                <div className="text-xs text-[#4B5565]">
                                    <div className="font-semibold text-[#131B2F]">No file content is available.</div>
                                    <div className="mt-0.5">
                                        This is a metadata-only record (zero-byte or canonical placeholder). Download is disabled and no file is served.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Canonical metadata grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <MetaCard title="Document">
                                <MetaRow icon={FileText} label="Title" value={doc.title || "—"} />
                                <MetaRow icon={Tag} label="Category" value={doc.category || "—"} />
                                <MetaRow icon={Calendar} label="Uploaded" value={humanDate(doc.uploaded_at)} />
                                {doc.uploaded_by_name && (
                                    <MetaRow icon={User} label="Uploaded by" value={`${doc.uploaded_by_name}${doc.uploaded_by_role ? ` · ${doc.uploaded_by_role}` : ""}`} />
                                )}
                                {typeof doc.version === "number" && doc.version > 1 && (
                                    <MetaRow icon={FileText} label="Version" value={`v${doc.version}`} />
                                )}
                                {doc.internal_note && (
                                    <MetaRow icon={FileText} label="Internal note" value={doc.internal_note} />
                                )}
                            </MetaCard>

                            <MetaCard title="Project & customer">
                                <MetaRow icon={FolderOpen} label="Project reference" value={doc.project_reference || "—"} />
                                <MetaRow icon={MapPin} label="Address" value={doc.project_address || "—"} />
                                <MetaRow icon={MapPin} label="Postcode" value={doc.project_postcode || "—"} />
                                <MetaRow icon={User} label="Customer" value={doc.customer_name || "—"} />
                                <MetaRow icon={Mail} label="Email" value={doc.customer_email || "—"} />
                            </MetaCard>
                        </div>
                    </div>
                )}

                <RelinkModal
                    open={relinkOpen}
                    doc={doc}
                    onCancel={() => setRelinkOpen(false)}
                    onDone={(updated) => {
                        setRelinkOpen(false);
                        setDoc(updated);
                        toast.success("Document relinked.");
                    }}
                />
                <ArchiveModal
                    open={archiveOpen}
                    doc={doc}
                    onCancel={() => setArchiveOpen(false)}
                    onDone={(updated) => {
                        setArchiveOpen(false);
                        setDoc(updated);
                        toast.success("Document archived. The file is preserved.");
                    }}
                />
            </PageContainer>
        </OperationsShell>
    );
}


function MetaCard({ title, children }) {
    return (
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5">
            <div className="text-[10px] uppercase tracking-wide text-[#4B5565] mb-3">{title}</div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function MetaRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2 text-sm">
            {Icon && <Icon size={14} className="mt-0.5 text-[#4B5565] shrink-0" />}
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-[#4B5565]">{label}</div>
                <div className="text-[#131B2F] break-words">{value}</div>
            </div>
        </div>
    );
}


function RelinkModal({ open, doc, onCancel, onDone }) {
    const [draft, setDraft] = useState("");
    const [projects, setProjects] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setDraft("");
        setProjects([]);
        setSelected(null);
        setBusy(false);
        setError(null);
        const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const runSearch = async (q) => {
        if (searching) return;
        setSearching(true);
        try {
            const r = await api.opsSearchProjectsForRelink(q, 20);
            setProjects(r.projects || []);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Search failed";
            setError({ code: null, message });
        } finally {
            setSearching(false);
        }
    };

    async function submit() {
        if (!selected || busy) return;
        setBusy(true);
        setError(null);
        try {
            const updated = await api.opsRelinkDocument(doc.id, {
                project_id: selected.id,
                expected_project_id: doc?.project_id || null,
            });
            onDone(updated);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = (typeof detail === "object" ? detail?.error_code : null) || null;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Relink failed";
            const humanised = code === "stale_update"
                ? "This document's association changed while you were reviewing it. Close this dialog and reload."
              : code === "project_not_found"
                ? "The selected project no longer exists."
              : code === "document_archived"
                ? "This document is archived. Restore it first, then relink."
              : e?.response?.status === 403
                ? "You do not have permission to relink documents."
              : message;
            setError({ code, message: humanised });
        } finally {
            setBusy(false);
        }
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid="ops-document-relink-modal"
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2F]">
                        <Link2 size={16} className="text-[#131B2F]" />
                        Relink document to a project
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="px-6 py-4 space-y-3 text-sm text-[#4B5565] flex-1 overflow-y-auto">
                    <div className="rounded-md border border-[#E5E7EB] bg-[#FAFAFC] p-3">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#4B5565]">Current association</div>
                        <div className="mt-1 text-xs text-[#131B2F]" data-testid="ops-document-relink-current">
                            {doc?.project_id ? (
                                <span>
                                    {doc.project_reference ? `${doc.project_reference} · ` : ""}
                                    {doc.project_exists ? (doc.customer_name || "—") : <span className="text-[#B91C1C]">Project no longer available ({doc.project_id})</span>}
                                </span>
                            ) : <span className="text-[#B45309]">No linked project</span>}
                        </div>
                    </div>

                    <form
                        onSubmit={(e) => { e.preventDefault(); runSearch(draft.trim()); }}
                        className="flex items-center gap-2"
                    >
                        <div className="flex-1 relative">
                            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                            <input
                                type="text"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Search by reference, address, customer name or email"
                                data-testid="ops-document-relink-search-input"
                                className="w-full h-10 pl-8 pr-3 rounded-md border border-[#E5E7EB] bg-white text-sm outline-none focus:border-[#131B2F]"
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={searching}
                            data-testid="ops-document-relink-search-btn"
                            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#131B2F]/60 bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-50"
                        >
                            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
                        </button>
                    </form>

                    <div className="rounded-md border border-[#E5E7EB] overflow-hidden max-h-64 overflow-y-auto" data-testid="ops-document-relink-results">
                        {projects.length === 0 ? (
                            <div className="p-4 text-xs text-[#4B5565] text-center" data-testid="ops-document-relink-empty">
                                {searching ? "Searching…" : "Search for a project to see identity-rich results here."}
                            </div>
                        ) : (
                            <ul className="divide-y divide-[#EDEFF3]">
                                {projects.map((p) => (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            onClick={() => setSelected(p)}
                                            data-testid={`ops-document-relink-option-${p.id}`}
                                            data-selected={selected?.id === p.id ? "true" : "false"}
                                            className={`w-full text-left px-3 py-2 hover:bg-[#F5F5F7] ${selected?.id === p.id ? "bg-[#EAF6E1]" : ""}`}
                                        >
                                            <div className="text-xs font-medium text-[#131B2F]">
                                                {p.reference || p.id} · {p.customer_name || "—"}
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-[#4B5565]">
                                                {p.address || "—"}
                                                {p.customer_email ? ` · ${p.customer_email}` : ""}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {selected && (
                        <div className="rounded-md border border-[#3E9E2E]/40 bg-[#EAF6E1]/40 p-3" data-testid="ops-document-relink-proposed">
                            <div className="text-[10px] uppercase tracking-[0.1em] text-[#3E9E2E]">Proposed replacement</div>
                            <div className="mt-1 text-xs text-[#131B2F]">
                                {selected.reference || selected.id} · {selected.customer_name || "—"}
                            </div>
                            <div className="mt-0.5 text-[11px] text-[#4B5565]">
                                Customer is derived from the selected project — no independent customer field is exposed.
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                             data-testid="ops-document-relink-error"
                             data-error-code={error.code || undefined}>
                            {error.message}
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="ops-document-relink-cancel"
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!selected || busy}
                        data-testid="ops-document-relink-ok"
                        className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0F1526] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Relink document
                    </button>
                </div>
            </div>
        </div>
    );
}


function ArchiveModal({ open, doc, onCancel, onDone }) {
    const [reason, setReason] = useState(ARCHIVE_REASON_DEFAULT);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setReason(ARCHIVE_REASON_DEFAULT);
        setBusy(false);
        setError(null);
        const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    async function submit() {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const updated = await api.opsArchiveDocument(doc.id, reason.trim() || ARCHIVE_REASON_DEFAULT);
            onDone(updated);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = (typeof detail === "object" ? detail?.error_code : null) || null;
            const message = (typeof detail === "object" ? detail?.detail : null)
                          || formatApiError(detail)
                          || "Archive failed";
            const humanised = code === "already_archived"
                ? "This document is already archived."
              : e?.response?.status === 403
                ? "You do not have permission to archive documents."
              : message;
            setError({ code, message: humanised });
        } finally {
            setBusy(false);
        }
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid="ops-document-archive-modal"
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2F]">
                        <Archive size={16} className="text-[#B45309]" />
                        Archive document as orphaned
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="px-6 py-4 space-y-3 text-sm text-[#4B5565]">
                    <div className="rounded-md border border-[#E5E7EB] bg-[#FAFAFC] p-3 text-xs" data-testid="ops-document-archive-target">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#4B5565]">Document</div>
                        <div className="mt-1 text-[#131B2F] font-medium truncate">{doc?.title || doc?.file_name}</div>
                        <div className="mt-0.5 text-[#4B5565]">{doc?.category || "—"} · {humanSize(doc?.file_size)}</div>
                    </div>
                    <p className="text-xs">
                        The file is <span className="font-semibold text-[#2E7E22]">preserved</span>. The record leaves the active Documents list but stays accessible under the Archived filter and can be restored at any time.
                    </p>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">
                            Reason
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            data-testid="ops-document-archive-reason"
                            rows={3}
                            maxLength={500}
                            className="w-full rounded-md border border-[#E5E7EB] bg-white p-3 text-sm outline-none focus:border-[#131B2F]"
                        />
                    </div>
                    {error && (
                        <div className="rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                             data-testid="ops-document-archive-error"
                             data-error-code={error.code || undefined}>
                            {error.message}
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="ops-document-archive-cancel"
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        data-testid="ops-document-archive-ok"
                        className="h-10 px-4 rounded-md bg-[#B45309] hover:bg-[#92400E] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Archive document
                    </button>
                </div>
            </div>
        </div>
    );
}
